import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Users,
  Search,
  Download,
  ChevronRight,
  ArrowUpDown,
  Archive,
  ArchiveRestore,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const AdminClients = () => {
  const { token, API, isSuperAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [archivedClients, setArchivedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [exporting, setExporting] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [viewMode, setViewMode] = useState("active"); // "active" | "archived"
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const [activeRes, archivedRes] = await Promise.all([
        fetch(`${API}/admin/clients`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/admin/clients?include_archived=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (activeRes.ok) setClients(await activeRes.json());
      if (archivedRes.ok) {
        const all = await archivedRes.json();
        setArchivedClients(all.filter((c) => c.is_archived));
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveToggle = async () => {
    if (!selectedClient) return;
    setArchiving(true);
    try {
      const response = await fetch(
        `${API}/admin/clients/${selectedClient.client_id}/archive`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setArchiveDialogOpen(false);
        setSelectedClient(null);
        fetchClients();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update client");
      }
    } catch {
      toast.error("Failed to update client");
    } finally {
      setArchiving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/admin/export/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          response.headers.get("Content-Disposition")?.split("filename=")[1] ||
          "clients_export.csv";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Clients exported successfully");
      } else {
        toast.error("Failed to export clients");
      }
    } catch {
      toast.error("Failed to export clients");
    } finally {
      setExporting(false);
    }
  };

  const getName = (c) => c.user?.name || c.name || "Unknown";

  const displayClients = viewMode === "active" ? clients : archivedClients;

  const filteredClients = displayClients
    .filter((client) => {
      const q = searchQuery.toLowerCase();
      return (
        getName(client).toLowerCase().includes(q) ||
        client.user?.email?.toLowerCase().includes(q) ||
        client.company_name?.toLowerCase().includes(q) ||
        client.email?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortField === "name") return getName(a).localeCompare(getName(b)) * dir;
      if (sortField === "hours") return ((a.hours_used || 0) - (b.hours_used || 0)) * dir;
      return 0;
    });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const activeCount = clients.length;

  return (
    <AdminLayout activeNav="clients">
      <div className="max-w-[1200px] mx-auto animate-fade-in">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1
              className="font-heading text-2xl font-bold text-rnb-navy tracking-tight"
              data-testid="clients-page-title"
            >
              {isSuperAdmin ? "Clients" : "My Clients"}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {activeCount} active{archivedClients.length > 0 && <> &middot; {archivedClients.length} archived</>} &middot; {activeCount + archivedClients.length} total
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
                className="text-xs gap-1.5 h-9 border-gray-200 text-gray-500 hover:text-rnb-navy"
                data-testid="export-clients-btn"
              >
                <Download className="h-3.5 w-3.5" />
                {exporting ? "Exporting..." : "Export"}
              </Button>
            )}
          </div>
        </div>

        {/* View Toggle Tabs */}
        {isSuperAdmin && (
          <div className="flex items-center gap-1 mb-5 bg-gray-100/80 rounded-xl p-1 w-fit" data-testid="view-toggle">
            <button
              onClick={() => setViewMode("active")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                viewMode === "active"
                  ? "bg-white text-rnb-navy shadow-sm"
                  : "text-gray-500 hover:text-rnb-navy"
              }`}
              data-testid="view-active-tab"
            >
              Active ({activeCount})
            </button>
            <button
              onClick={() => setViewMode("archived")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                viewMode === "archived"
                  ? "bg-white text-rnb-navy shadow-sm"
                  : "text-gray-500 hover:text-rnb-navy"
              }`}
              data-testid="view-archived-tab"
            >
              <Archive className="h-3.5 w-3.5" />
              Archived ({archivedClients.length})
            </button>
          </div>
        )}

        {/* Search Bar */}
        <div className="relative mb-5">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full max-w-md pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl text-rnb-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rnb-blue/20 focus:border-rnb-blue/30 transition-all"
            data-testid="client-search"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-rnb-blue/20 border-t-rnb-blue rounded-full animate-spin" />
          </div>
        ) : (
          <div data-testid="clients-list">
            {/* Column Header */}
            <div className="grid grid-cols-12 gap-4 px-5 py-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
              <div className="col-span-4 flex items-center gap-1">
                <button onClick={() => toggleSort("name")} className="flex items-center gap-1 hover:text-rnb-navy transition-colors" data-testid="sort-name-btn">
                  Client <ArrowUpDown className="w-3 h-3" />
                </button>
              </div>
              <div className="col-span-3">Company</div>
              <div className="col-span-2 flex items-center gap-1">
                <button onClick={() => toggleSort("hours")} className="flex items-center gap-1 hover:text-rnb-navy transition-colors" data-testid="sort-hours-btn">
                  Hours <ArrowUpDown className="w-3 h-3" />
                </button>
              </div>
              {isSuperAdmin && <div className="col-span-2">{viewMode === "active" ? "Consultant" : "Archived"}</div>}
              <div className={isSuperAdmin ? "col-span-1" : "col-span-3"}>
                {viewMode === "active" ? "Status" : "Actions"}
              </div>
            </div>

            {/* Rows */}
            {filteredClients.length > 0 ? (
              <div className="space-y-2">
                {filteredClients.map((client) => {
                  const name = getName(client);
                  const initials = name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2);
                  const hoursUsed = (client.hours_used || 0).toFixed(1);
                  const monthlyHours = client.monthly_hours || 0;
                  const hoursPercent = monthlyHours > 0 ? Math.min((client.hours_used || 0) / monthlyHours * 100, 100) : 0;
                  const isArchived = client.is_archived;

                  return (
                    <div
                      key={client.client_id}
                      className={`grid grid-cols-12 gap-4 px-5 py-3.5 items-center bg-white rounded-xl hover:shadow-md transition-all duration-200 group ${isArchived ? "opacity-70" : ""}`}
                      data-testid={`client-row-${client.client_id}`}
                    >
                      {/* Name — clickable link */}
                      <Link
                        to={`/admin/clients/${client.client_id}`}
                        className="col-span-4 flex items-center gap-3 min-w-0"
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-semibold shrink-0 ${
                          isArchived
                            ? "bg-gray-400"
                            : "bg-gradient-to-br from-rnb-navy/90 to-rnb-blue"
                        }`}>
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-rnb-navy truncate">{name}</p>
                          <p className="text-xs text-gray-400 truncate">
                            {client.user?.email || client.email}
                          </p>
                        </div>
                      </Link>

                      {/* Company */}
                      <div className="col-span-3 min-w-0">
                        <p className="text-sm text-gray-500 truncate">
                          {client.company_name || "—"}
                        </p>
                      </div>

                      {/* Hours */}
                      <div className="col-span-2">
                        <div className="flex items-baseline gap-1">
                          <span className="font-accent text-sm font-semibold text-rnb-navy">
                            {hoursUsed}
                          </span>
                          <span className="text-xs text-gray-400">/ {monthlyHours}h</span>
                        </div>
                        <div className="w-full h-1 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              hoursPercent > 90 ? "bg-red-400" : hoursPercent > 70 ? "bg-amber-400" : "bg-rnb-teal"
                            }`}
                            style={{ width: `${hoursPercent}%` }}
                          />
                        </div>
                      </div>

                      {/* Consultant / Archived info */}
                      {isSuperAdmin && (
                        <div className="col-span-2 min-w-0">
                          {viewMode === "archived" ? (
                            <p className="text-xs text-gray-400">
                              {client.archived_at
                                ? new Date(client.archived_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                : "\u2014"}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 truncate">
                              {client.consultant?.name || (
                                <span className="text-gray-300">Unassigned</span>
                              )}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Status + Actions */}
                      <div className={`${isSuperAdmin ? "col-span-1" : "col-span-3"} flex items-center justify-end gap-2`}>
                        {isSuperAdmin && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedClient(client);
                              setArchiveDialogOpen(true);
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isArchived
                                ? "text-rnb-teal hover:bg-rnb-teal/10"
                                : "text-gray-400 hover:text-amber-600 hover:bg-amber-50"
                            }`}
                            title={isArchived ? "Restore client" : "Archive client"}
                            data-testid={`archive-btn-${client.client_id}`}
                          >
                            {isArchived ? (
                              <ArchiveRestore className="w-4 h-4" />
                            ) : (
                              <Archive className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {!isArchived && (
                          <Link to={`/admin/clients/${client.client_id}`}>
                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-rnb-blue group-hover:translate-x-0.5 transition-all duration-200" />
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-xl py-16 text-center">
                {viewMode === "archived" ? (
                  <>
                    <Archive className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No archived clients</p>
                  </>
                ) : (
                  <>
                    <Users className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">
                      {searchQuery
                        ? "No clients match your search"
                        : isSuperAdmin
                        ? "No clients yet"
                        : "No clients assigned yet"}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Archive/Restore Confirmation Dialog */}
        <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedClient?.is_archived ? (
                  <ArchiveRestore className="h-5 w-5 text-rnb-teal" />
                ) : (
                  <Archive className="h-5 w-5 text-amber-500" />
                )}
                {selectedClient?.is_archived ? "Restore Client" : "Archive Client"}
              </DialogTitle>
              <DialogDescription>
                {selectedClient?.is_archived
                  ? "This will restore the client to the active list and include them in analytics."
                  : "Archived clients will be hidden from active lists and excluded from analytics. You can restore them anytime."}
              </DialogDescription>
            </DialogHeader>
            {selectedClient && (
              <div className="py-3">
                <div className="bg-slate-50 p-4 rounded-lg flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rnb-navy/90 to-rnb-blue flex items-center justify-center text-white text-sm font-semibold">
                    {getName(selectedClient).split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <p className="font-medium text-rnb-navy">{getName(selectedClient)}</p>
                    <p className="text-xs text-gray-400">{selectedClient.company_name || selectedClient.email}</p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleArchiveToggle}
                disabled={archiving}
                className={selectedClient?.is_archived
                  ? "bg-rnb-teal hover:bg-rnb-teal/90"
                  : "bg-amber-500 hover:bg-amber-600"
                }
                data-testid="confirm-archive-btn"
              >
                {archiving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {selectedClient?.is_archived ? "Restore" : "Archive"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminClients;
